interface EnvConfig {
  apiBaseUrl: string
  baseDomain: string
  cognito: {
    region: string
    userPoolId: string
    clientId: string
  }
  features: {
    enableSecurity: boolean
    enableGeocoding: boolean
    enableDashboards: boolean
    enableAI: boolean
  }
  isDevelopment: boolean
  isProduction: boolean
}

function getEnvVar(key: string, defaultValue?: string): string {
  const value = import.meta.env[key]

  if (value === undefined && defaultValue === undefined) {
    // Deliberate boot-time diagnostic in a foundational config util — importing
    // the app logger here risks an import cycle.
    // eslint-disable-next-line no-console
    console.warn(`Environment variable ${key} is not defined`)

    return ''
  }

  return value ?? defaultValue ?? ''
}

function getBoolEnvVar(key: string, defaultValue: boolean = false): boolean {
  const value = import.meta.env[key]

  if (value === undefined) {
    return defaultValue
  }

  return value === 'true' || value === '1'
}

/**
 * Resolve the API base URL at runtime by inspecting the current page's hostname.
 *
 * - `console.{baseDomain}` → `https://console.api.{baseDomain}` (central)
 * - `<tenant>.console.{baseDomain}` → `https://<tenant>.api.{baseDomain}` (tenant)
 * - Anything else (localhost, vite preview, etc.) → falls through to the
 *   `VITE_API_BASE_URL` env override, defaulting to `/api` (relative).
 *
 * The override path is also honored unconditionally for build-time pinning during
 * local dev (`VITE_API_BASE_URL=http://localhost:3000`) and CI fixtures.
 *
 * Single-bundle multi-tenant: the same SPA bundle serves every tenant subdomain;
 * tenant identity is derived per-request from window.location.
 */
export function deriveApiBaseUrl(): string {
  const override = getEnvVar('VITE_API_BASE_URL', '')
  if (override) return override

  const baseDomain = getEnvVar('VITE_BASE_DOMAIN', 'gisify.dev')

  if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
    const host = window.location.hostname

    if (host === `console.${baseDomain}`) {
      return `https://console.api.${baseDomain}`
    }

    const consoleSuffix = `.console.${baseDomain}`

    if (host.endsWith(consoleSuffix)) {
      const tenantId = host.slice(0, -consoleSuffix.length)

      if (tenantId && !tenantId.includes('.')) {
        return `https://${tenantId}.api.${baseDomain}`
      }
    }
  }

  return '/api'
}

export const env: EnvConfig = {
  apiBaseUrl: deriveApiBaseUrl(),
  baseDomain: getEnvVar('VITE_BASE_DOMAIN', 'gisify.dev'),

  cognito: {
    region: getEnvVar('VITE_COGNITO_REGION', 'ap-southeast-1'),
    userPoolId: getEnvVar('VITE_COGNITO_USER_POOL_ID', ''),
    clientId: getEnvVar('VITE_COGNITO_CLIENT_ID', ''),
  },

  features: {
    enableSecurity: getBoolEnvVar('VITE_FEATURE_SECURITY', true),
    enableGeocoding: getBoolEnvVar('VITE_FEATURE_GEOCODING', true),
    enableDashboards: getBoolEnvVar('VITE_FEATURE_DASHBOARDS', true),
    enableAI: getBoolEnvVar('VITE_FEATURE_AI', true),
  },

  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
}

export function getTenantId(): string | null {
  const hostname = window.location.hostname
  const parts = hostname.split('.')

  if (parts.length >= 3 && parts[1] === 'console') {
    return parts[0]
  }

  if (parts.length >= 2 && !['localhost', 'console', 'api', 'www'].includes(parts[0])) {
    return parts[0]
  }

  return localStorage.getItem('gisify_tenant_id')
}

/**
 * Runtime API-base override, set from `/config.json` at boot (before React
 * mounts) via {@link setRuntimeApiBaseUrl}. On-prem the API lives at a
 * per-deployment host (`https://api.${ROOT_DOMAIN}`) that the build-time
 * {@link deriveApiBaseUrl} can't know — it only recognises the cloud
 * `console.{domain}` shapes and otherwise falls through to `/api` (relative),
 * which on-prem resolves to the SPA's own origin and serves index.html. When
 * config.json carries `apiBaseUrl` this override takes precedence; cloud's
 * config.json omits it, so the derived value is used unchanged.
 */
let runtimeApiBaseUrl: string | null = null

/** Set the runtime API base (idempotent). Called by loadAuthConfig at boot. */
export function setRuntimeApiBaseUrl(url: string): void {
  const trimmed = url.trim()
  runtimeApiBaseUrl = trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed
}

export function getApiUrl(path: string): string {
  const resolved = runtimeApiBaseUrl ?? env.apiBaseUrl
  const baseUrl = resolved.endsWith('/') ? resolved.slice(0, -1) : resolved
  const cleanPath = path.startsWith('/') ? path : `/${path}`

  return `${baseUrl}${cleanPath}`
}

export default env
